import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);

export interface FilterObject {
    [key: string]: string | string[];
}

export enum FillerFieldType {
    input = 'input',
    select = 'select',
    tagSelect = 'tagSelect',
    tagMultiSelect = 'tagMultiSelect',
    multiSelect = 'multiSelect',
    dateRangePicker = 'datePicker',
    toggle = 'toggle',
    autocomplete = 'autocomplete',
    amountRange = 'amountRange',
    cardSelector = 'cardSelector',
    orderSelector = 'orderSelector',
}

export const ListOfMultiValuedFilterType = [
    FillerFieldType.tagMultiSelect,
    FillerFieldType.multiSelect,
];

export interface AfterSelectInstruction {
    type: string;
    filterAppliedFor: string;
    valueApplied: any;
}

export interface FilterFieldDisabledCondition {
    watchedField: string;
    // A list matches when the watched field holds any one of the values, which is how a
    // filter stays visible for more than one state of the field that gates it.
    value: any;
}

export const matchesFieldCondition = (
    condition: FilterFieldDisabledCondition | undefined,
    watchedValue: any,
): boolean => {
    if (!condition) {
        return false;
    }
    return Array.isArray(condition.value)
        ? condition.value.includes(watchedValue)
        : condition.value === watchedValue;
};

export interface AdvanceFilterOptionNew {
    fieldType: FillerFieldType;
    name: string;
    label: string;
    defaultValue: any;
    options?: any[];
    optionsMultiselectSelected?: any[];
    selected?: any;
    disabled?: boolean;
    onChange?: (value: string) => void;
    afterSelectInstruction?: AfterSelectInstruction;
    isDisabledCondition?: FilterFieldDisabledCondition;
    // Disables the filter from the blade's current values, for rules that depend on more than
    // one field — e.g. two groups of filters that exclude each other.
    isDisabled?: (values: Record<string, any>) => boolean;
    fieldToTriggerAfterChange?: string;
    renderValueFunction?: Function;
    tooltip?: string;
    isHideCondition?: FilterFieldDisabledCondition;
    // Renders a search affordance inside an `input` filter, for filters that read as a lookup.
    searchIcon?: boolean;
}

export const hasFilterValue = (value: any): boolean =>
    Array.isArray(value) ? value.length > 0 : !!value;

export const getCurrentURLHashQuery = () => {
    return window.location.hash.split('#')[1];
};

export const updateObjectValues = (obj1: Record<string, any>, obj2: Record<string, any>): any => {
    const result = { ...obj1 };
    for (const key in obj2) {
        if (obj2.hasOwnProperty(key) && obj1.hasOwnProperty(key)) {
            result[key] = obj2[key];
        }
    }
    return result;
};

export const countNonEmptyProperties = (obj: Record<string, any>): number => {
    let count = 0;
    for (const key in obj) {
        if (obj.hasOwnProperty(key)) {
            const value = obj[key];
            if (
                value !== null &&
                value !== undefined &&
                value !== '' &&
                value.length !== 0 &&
                value !== false
            ) {
                count++;
            }
        }
    }
    return count;
};

export const parseFilterString = (
    filterString: string,
    options: AdvanceFilterOptionNew[],
): FilterObject => {
    const filterObject: FilterObject = {};
    if (!filterString) return filterObject;
    const keyValuePairs = filterString.split('&');
    if (keyValuePairs?.length < 1) return filterObject;
    keyValuePairs.forEach((pair) => {
        const [key, value] = pair.split('=');
        if (key && value) {
            const decodedValue = decodeURIComponent(value);
            if (decodedValue.includes(',')) {
                filterObject[key] = decodedValue.split(',');
            } else {
                // to understand if it should be an array in case of single value
                const findOption = options.find((o) => o.name === key);
                if (
                    findOption?.fieldType &&
                    ListOfMultiValuedFilterType.indexOf(findOption.fieldType) > -1
                ) {
                    filterObject[key] = [decodedValue];
                } else {
                    filterObject[key] = decodedValue;
                }
            }
        }
    });

    return filterObject;
};

export const createFilterHash = (filterObject: FilterObject): string => {
    if (!filterObject || Object.keys(filterObject).length === 0) return '';
    if (countNonEmptyProperties(filterObject) < 1) return '';
    const keyValuePairs = Object.entries(filterObject).map(([key, value]) => {
        if (Array.isArray(value)) {
            if (value.length > 0) {
                return `${key}=${value.map((val) => encodeURIComponent(val)).join('%2C')}`;
            }
        } else {
            if (value) {
                return `${key}=${encodeURIComponent(value)}`;
            }
        }
    });
    const clearedArray = keyValuePairs.filter((item) => !!item);
    return clearedArray.join('&');
};

export const DATE_FORMAT = 'YYYY-MM-DD';
export const convertDayJsUnixDataToOffsetDateTimeFormat = (data: any) => {
    return dayjs.unix(+data).utc().format(DATE_FORMAT);
};

export const returnClearedFiltersObject = (options: AdvanceFilterOptionNew[]) => {
    const clearedObj: Record<string, any> = {};
    options.forEach((options) => {
        if (ListOfMultiValuedFilterType.indexOf(options.fieldType) > -1) {
            clearedObj[options.name] = [];
        } else {
            clearedObj[options.name] = '';
        }
    });
    return clearedObj;
};
